import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import type { LOANREQUEST_STATUSES, SUMMARY_ROUTE } from 'cpool-constants';
import { AGGREGATE_NAMES, LOAN_REQUEST_COMMANDS, LOANREQUEST_DEFAULT_STATUS, SUMMARY_ROUTES } from 'cpool-constants';
import type { Schema_Status } from 'cpool-schemas/src/LoanRequest/status/status';
import type { SchemaLoanRequestResolver_GetSummary } from 'cpool-tables/backend-resolvers';
import { createId } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { isBadRequestError } from './utils/isBadRequestError';
import { SubstatusCommandError } from './utils/SubstatusCommandError';
import { APICommunicator } from '~/api/APICommunicator';
import { LEAD_LIST_DEFAULT_FILTERS, PAGINATION } from '~/constants';
import { verifyLoanRequestMailStatus } from '~/features/leadDetails/components/utils/verifyLoanRequestMailStatus';
import type { LeadListFilter } from '~/features/leadList/schemas/route.schema';
import { LEAD_LIST_ACTION_FILTER } from '~/features/leadList/utils/actionFilters/leadListActionFilter';
import { buildLoanRequestGetSummarySearchParams } from '~/features/leadList/utils/buildLoanRequestGetSummarySearchParams';
import useUserInfo from '~/hooks/useUserInfo';
import { setQueryPosition, useQueryPositionStore } from '~/store/queryPositionStore';
import { appendPositionToQueryKey } from '~/utils/appendPositionToQueryKey';
import { invalidateResubmissionQueries } from '~/utils/invalidateResubmissionQueries';
import { queryKeys } from '~/utils/queryKeys';

type MutationVariables = {
    currentStatus: Schema_Status['status'];
    currentSubStatus?: string | null;
    leadId: string;
    noteContent?: string;
    resubmissionTimestamp?: number;
    statusData: Exclude<Schema_Status, { status: (typeof LOANREQUEST_STATUSES)['CANCELED'] }>;
};

type MutationContext = {
    hasStatusChanged: boolean;
    previousStatusData: unknown;
    previousSummary: SchemaLoanRequestResolver_GetSummary | undefined;
};

const isStatusUnchanged = (
    statusData: MutationVariables['statusData'],
    currentStatus: MutationVariables['currentStatus'],
    currentSubStatus: MutationVariables['currentSubStatus'],
) => statusData.status === currentStatus && statusData.subStatus === (currentSubStatus ?? undefined);

export const useSubstatusMutation = () => {
    const queryClient = useQueryClient();
    const { i18n, t } = useTranslation();
    const user = useUserInfo();
    const leadsMatch = useRouterState({
        select: (state) =>
            state.matches.find(
                (match) => match.routeId === SUMMARY_ROUTES.LEADS || match.routeId === SUMMARY_ROUTES.BUCKET_LEADS,
            ) ?? null,
    });

    const isLeadsRouteActive = !!leadsMatch;
    const search = (leadsMatch?.search as Partial<LeadListFilter>) ?? {};
    const {
        adviser = LEAD_LIST_DEFAULT_FILTERS.adviser,
        bank = LEAD_LIST_DEFAULT_FILTERS.bank,
        bucket = LEAD_LIST_DEFAULT_FILTERS.bucket,
        page = LEAD_LIST_DEFAULT_FILTERS.page,
        pageSize = PAGINATION.LEAD_LIST.DEFAULT_PAGE_SIZE,
        partnerLevel = LEAD_LIST_DEFAULT_FILTERS.partnerLevel,
        resubmission = LEAD_LIST_DEFAULT_FILTERS.resubmission,
        status = LOANREQUEST_DEFAULT_STATUS.ALL,
        subStatus = LEAD_LIST_DEFAULT_FILTERS.subStatus,
        tag = LEAD_LIST_DEFAULT_FILTERS.tag,
    } = search;
    const filterForSummary = {
        adviser,
        bank,
        bucket,
        page,
        pageSize,
        partnerLevel,
        resubmission,
        status,
        subStatus,
        tag,
    };
    const route = (leadsMatch?.routeId as SUMMARY_ROUTE | undefined) ?? SUMMARY_ROUTES.LEADS;
    const summaryParamsWithoutPosition = buildLoanRequestGetSummarySearchParams({
        filter: filterForSummary,
        route,
    });
    const summaryKeyWithoutPosition = queryKeys.getSummary({
        filters: summaryParamsWithoutPosition.filters,
        limit: summaryParamsWithoutPosition.limit,
        offset: summaryParamsWithoutPosition.offset,
        permissionsActionFilter: LEAD_LIST_ACTION_FILTER,
        route,
        status,
    });
    const queryPosition = useQueryPositionStore((state) => state.positions[JSON.stringify(summaryKeyWithoutPosition)]);
    const summaryParams = buildLoanRequestGetSummarySearchParams({
        filter: filterForSummary,
        permissionsActionFilter: LEAD_LIST_ACTION_FILTER,
        position: queryPosition,
        route,
    });
    const getSummaryKey = queryKeys.getSummary(summaryParams);
    const getCountsKey = queryKeys.getCounts({
        filters: summaryParams.filters,
        route: leadsMatch?.routeId as SUMMARY_ROUTE | undefined,
        status,
    });

    const showCommandErrorToast = (error: Error) => {
        if (!(error instanceof SubstatusCommandError)) return;
        if (error.command === LOAN_REQUEST_COMMANDS.setResubmission) {
            toast.error(t(isBadRequestError(error.cause) ? 'RESUBMISSION.TIME_PAST' : 'RESUBMISSION.ERROR'));
        } else if (error.command === LOAN_REQUEST_COMMANDS.changeStatus) {
            toast.error(t('STATUS.ERRORS.CHANGE_FAILED'));
        } else {
            toast.error(t('LEADS.NOTES.CREATION_ERROR'));
        }
    };

    return useMutation({
        mutationFn: async ({
            currentStatus,
            currentSubStatus,
            leadId,
            noteContent,
            resubmissionTimestamp,
            statusData,
        }: MutationVariables) => {
            let resubmissionPosition: number | null = null;
            const sendStep = async <T>(command: SubstatusCommandError['command'], send: () => Promise<T>) => {
                try {
                    return await send();
                } catch (error) {
                    throw new SubstatusCommandError(command, resubmissionPosition, error);
                }
            };

            // sent first so a failed setResubmission leaves status and notes untouched
            const resubmissionResult =
                resubmissionTimestamp === undefined
                    ? null
                    : await sendStep(LOAN_REQUEST_COMMANDS.setResubmission, () =>
                          APICommunicator.sendCommand({
                              aggregateId: leadId,
                              aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                              commandName: LOAN_REQUEST_COMMANDS.setResubmission,
                              payload: { timestamp: resubmissionTimestamp },
                          }),
                      );
            resubmissionPosition = resubmissionResult?.position ?? null;
            const changeStatusResult = isStatusUnchanged(statusData, currentStatus, currentSubStatus)
                ? null
                : await sendStep(LOAN_REQUEST_COMMANDS.changeStatus, () =>
                      APICommunicator.sendCommand({
                          aggregateId: leadId,
                          aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                          commandName: LOAN_REQUEST_COMMANDS.changeStatus,
                          payload: {
                              sendEmail: statusData.sendEmail,
                              status: statusData.status,
                              subStatus: statusData.subStatus,
                              userId: user?.id,
                          },
                      }),
                  );
            const trimmedNote = noteContent?.trim();
            const noteResult = trimmedNote
                ? await sendStep(LOAN_REQUEST_COMMANDS.createNote, () =>
                      APICommunicator.sendCommand({
                          aggregateId: leadId,
                          aggregateName: AGGREGATE_NAMES.LOAN_REQUEST,
                          commandName: LOAN_REQUEST_COMMANDS.createNote,
                          payload: {
                              author: {
                                  firstName: user?.firstName,
                                  id: user?.id,
                                  lastName: user?.lastName,
                              },
                              content: trimmedNote,
                              favored: false,
                              id: createId(),
                          },
                      }),
                  )
                : null;

            return { changeStatusResult, noteResult, resubmissionResult };
        },
        onError: (error, { leadId, resubmissionTimestamp }, context: MutationContext | undefined) => {
            showCommandErrorToast(error);
            const isResubmissionSaved = error instanceof SubstatusCommandError && !!error.resubmissionPosition;
            if (isResubmissionSaved) toast.success(t('RESUBMISSION.SUCCESS'));
            if (!context) return;

            if (context.hasStatusChanged) {
                queryClient.setQueryData(
                    appendPositionToQueryKey.current(queryKeys.getLoanRequestStatusData(leadId)),
                    context.previousStatusData,
                );
            }
            if (isLeadsRouteActive) {
                queryClient.setQueryData(
                    getSummaryKey,
                    isResubmissionSaved && resubmissionTimestamp !== undefined
                        ? context.previousSummary?.map((lead) =>
                              lead.id === leadId ? { ...lead, resubmission: resubmissionTimestamp } : lead,
                          )
                        : context.previousSummary,
                );
            }
        },
        onMutate: async ({
            currentStatus,
            currentSubStatus,
            leadId,
            resubmissionTimestamp,
            statusData,
        }): Promise<MutationContext | undefined> => {
            const hasStatusChanged = !isStatusUnchanged(statusData, currentStatus, currentSubStatus);
            const hasResubmission = resubmissionTimestamp !== undefined;
            if (!hasStatusChanged && !hasResubmission) return undefined;

            let previousStatusData: unknown;
            if (hasStatusChanged) {
                const detailKey = appendPositionToQueryKey.current(queryKeys.getLoanRequestStatusData(leadId));
                previousStatusData = queryClient.getQueryData(detailKey);
                queryClient.setQueryData(detailKey, {
                    status: statusData.status,
                    subStatus: statusData.subStatus,
                });
            }

            let previousSummary: SchemaLoanRequestResolver_GetSummary | undefined;
            if (isLeadsRouteActive) {
                await queryClient.cancelQueries({ queryKey: getSummaryKey });
                previousSummary = queryClient.getQueryData<SchemaLoanRequestResolver_GetSummary>(getSummaryKey);
                queryClient.setQueryData(getSummaryKey, (data: SchemaLoanRequestResolver_GetSummary | undefined) =>
                    data?.map((lead) =>
                        lead.id === leadId
                            ? {
                                  ...lead,
                                  ...(hasStatusChanged && {
                                      status: statusData.status,
                                      subStatus: statusData.subStatus ?? null,
                                  }),
                                  ...(hasResubmission && { resubmission: resubmissionTimestamp }),
                              }
                            : lead,
                    ),
                );
            }

            return { hasStatusChanged, previousStatusData, previousSummary };
        },
        onSettled: async (data, error, { leadId, resubmissionTimestamp }) => {
            await queryClient.invalidateQueries({ queryKey: queryKeys.getLoanRequestStatusData(leadId) });
            const resubmissionPosition =
                data?.resubmissionResult?.position ??
                (error instanceof SubstatusCommandError ? error.resubmissionPosition : null);
            if (resubmissionPosition && isLeadsRouteActive) {
                // later commands of this save have higher positions
                const lastPosition =
                    data?.noteResult?.position ?? data?.changeStatusResult?.position ?? resubmissionPosition;
                setQueryPosition(JSON.stringify(summaryKeyWithoutPosition), lastPosition);
            }
            // also refetches the lead list summary
            const hasResubmission = resubmissionTimestamp !== undefined;
            if (hasResubmission) {
                await invalidateResubmissionQueries(queryClient, leadId, resubmissionPosition);
            }
            if (error || !data) {
                // The mutation can fail after the status change already succeeded on the
                // server (e.g. only createNote failed), so refetch the summary to be safe.
                if (isLeadsRouteActive && !hasResubmission) {
                    await queryClient.invalidateQueries({ queryKey: getSummaryKey });
                }
                return;
            }

            if (data.changeStatusResult?.position && isLeadsRouteActive) {
                await queryClient.invalidateQueries({ queryKey: getCountsKey });
            }
            if (data.noteResult?.position) {
                setQueryPosition(JSON.stringify(queryKeys.getLoanRequestNotes(leadId)), data.noteResult.position);
                await queryClient.invalidateQueries({ queryKey: queryKeys.getLoanRequestNotes(leadId) });
            }
            if (
                isLeadsRouteActive &&
                !hasResubmission &&
                (data.changeStatusResult?.position || data.noteResult?.position)
            ) {
                await queryClient.invalidateQueries({ queryKey: getSummaryKey });
            }
        },
        onSuccess: ({ changeStatusResult, noteResult }, { leadId, statusData }) => {
            if (changeStatusResult && statusData.sendEmail) {
                // not awaited: mutate's onSuccess (closes the dialog) would wait for mail delivery
                verifyLoanRequestMailStatus({
                    aggregateId: leadId,
                    correlationId: changeStatusResult.id,
                    i18n,
                    t,
                });
            }
            if (changeStatusResult?.position) {
                setQueryPosition(JSON.stringify(queryKeys.find()), changeStatusResult.position);
                setQueryPosition(
                    JSON.stringify(queryKeys.getLoanRequestStatusData(leadId)),
                    changeStatusResult.position,
                );
            }
            if (noteResult?.position) {
                setQueryPosition(JSON.stringify(queryKeys.getLoanRequestNotes(leadId)), noteResult.position);
            }
        },
    });
};
